/*
 * Turns the compose form into the body of POST /api/v1/push/notification.
 *
 * The shape comes from th-b2c-techupgrade-be-notification
 * src/model/RequestBody/PushNotification.ts :
 *
 *   {
 *     data: { [key: string]: string },        // required
 *     notification?: { title?, body? },
 *     android?: { ttl?, priority?, notification?: { icon?, color? } },
 *     apns?: { payload?: { aps?: { contentAvailable? } } },
 *     token?: string        // exactly one target per request
 *     topic?: string        // (either token or topic, never both)
 *   }
 *
 * Anything else (notification.image, condition, fcmOptions, …) comes back as a 400:
 * the controller validates with forbidNonWhitelisted. Options the Firebase console
 * offers but the backend does not accept yet ride along inside `data` instead.
 */

import { PLATFORM_TOPICS } from "./config";

const TOPIC_PATTERN = /^[a-zA-Z0-9\-_.~%]+$/;

const splitLines = value =>
  (value || "")
    .split(/[\n,]/)
    .map(item => item.trim())
    .filter(item => item !== "");

/** Every value in an FCM data map has to be a string */
const asString = value => {
  if (value === null || value === undefined) {
    return "";
  }
  return typeof value === "string" ? value : String(value);
};

/** Every target this form will fire at (one target per request) */
export const buildTargets = form => {
  const targets = [];

  if (form.targetMode === "ALL") {
    if (form.platforms.ios) {
      targets.push({ kind: "topic", value: PLATFORM_TOPICS.ios, label: "All iOS devices" });
    }
    if (form.platforms.android) {
      targets.push({ kind: "topic", value: PLATFORM_TOPICS.android, label: "All Android devices" });
    }
  }

  if (form.targetMode === "TOPIC") {
    splitLines(form.topics).forEach(topic => {
      targets.push({ kind: "topic", value: topic, label: `topic: ${topic}` });
    });
  }

  if (form.targetMode === "USER") {
    // At login the app subscribes to topics named after its userId (customer uuid) and unitholderId
    splitLines(form.users).forEach(user => {
      targets.push({ kind: "topic", value: user, label: `user/unitholder: ${user}` });
    });
  }

  if (form.targetMode === "TOKEN") {
    splitLines(form.tokens).forEach(token => {
      targets.push({
        kind: "token",
        value: token,
        label: `device: ${token.slice(0, 12)}…`
      });
    });
  }

  return targets;
};

/** The data map attached to every target */
export const buildData = form => {
  const data = {};

  if (form.dataType) {
    data.type = asString(form.dataType);
  }
  if (form.campaignName) {
    data.campaign_name = asString(form.campaignName);
  }
  if (form.imageUrl) {
    // The backend does not accept notification.image, so the app reads it from data
    data.image = asString(form.imageUrl);
  }
  if (form.deeplink) {
    data.deeplink = asString(form.deeplink);
  }
  if (form.conversionEvent) {
    data.conversion_event = asString(form.conversionEvent);
  }
  if (form.androidChannelId) {
    data.channel_id = asString(form.androidChannelId);
  }
  if (form.iosBadge !== "" && form.iosBadge !== null && form.iosBadge !== undefined) {
    data.badge = asString(form.iosBadge);
  }
  if (form.sound === false) {
    data.sound = "false";
  }

  (form.customData || []).forEach(({ key, value }) => {
    const trimmedKey = (key || "").trim();
    if (trimmedKey !== "") {
      data[trimmedKey] = asString(value);
    }
  });

  if (Object.keys(data).length === 0) {
    // data is required by the backend — covers a form with nothing filled in
    data.source = "admin_dashboard";
  }

  return data;
};

const buildAndroid = form => {
  const android = {};

  if (form.priority) {
    android.priority = form.priority;
  }
  // firebase-admin measures AndroidConfig.ttl in milliseconds
  const hours = parseFloat(form.ttlHours);
  if (!isNaN(hours) && hours > 0) {
    android.ttl = Math.round(hours * 60 * 60 * 1000);
  }

  const notification = {};
  if (form.androidIcon) {
    notification.icon = form.androidIcon;
  }
  if (form.androidColor) {
    notification.color = form.androidColor;
  }
  if (Object.keys(notification).length > 0) {
    android.notification = notification;
  }

  return Object.keys(android).length > 0 ? android : null;
};

const buildApns = form =>
  form.contentAvailable ? { payload: { aps: { contentAvailable: true } } } : null;

/** Body for a single target */
export const buildPayload = (form, target) => {
  const payload = { data: buildData(form) };

  if (form.title || form.body) {
    payload.notification = {
      ...(form.title ? { title: form.title } : {}),
      ...(form.body ? { body: form.body } : {})
    };
  }

  const android = buildAndroid(form);
  if (android) {
    payload.android = android;
  }

  const apns = buildApns(form);
  if (apns) {
    payload.apns = apns;
  }

  if (target) {
    payload[target.kind] = target.value;
  }

  return payload;
};

/** One request per target: the backend takes a single token or topic at a time */
export const buildRequests = form =>
  buildTargets(form).map(target => ({ target, payload: buildPayload(form, target) }));

/*
 * Firebase recommends scheduling a send a few minutes ahead so FCM can spread the load and still
 * deliver on time, especially for event/time-based notifications. So rather than only refusing a
 * time in the past, the form refuses one that is less than this many minutes from now. Keep the
 * rule in one place: change the buffer here and every validator and message follows.
 */
export const MIN_SCHEDULE_LEAD_MINUTES = 5;

export const SCHEDULE_LEAD_MESSAGE = `Schedule the send at least ${MIN_SCHEDULE_LEAD_MINUTES} minutes from now`;

// A scheduled send that is now, in the past, or sooner than the lead time cannot be saved.
export const scheduleTooSoon = form =>
  form.scheduleMode === "SCHEDULED" &&
  Boolean(form.scheduledAt) &&
  new Date(form.scheduledAt).getTime() < Date.now() + MIN_SCHEDULE_LEAD_MINUTES * 60 * 1000;

/** Validates before sending — returns an array of error messages */
export const validateForm = form => {
  const errors = [];

  if (!(form.body || "").trim()) {
    errors.push("Notification text is required");
  }
  if ((form.title || "").length > 200) {
    errors.push("Title exceeds 200 characters");
  }

  const targets = buildTargets(form);
  if (targets.length === 0) {
    errors.push("Select at least 1 target");
  }

  targets.forEach(target => {
    if (target.kind === "topic" && !TOPIC_PATTERN.test(target.value)) {
      errors.push(`topic "${target.value}" has characters Firebase does not support (only a-z A-Z 0-9 - _ . ~ % are allowed)`);
    }
    if (target.kind === "topic" && target.value.length > 900) {
      errors.push(`topic "${target.value.slice(0, 20)}…" exceeds 900 characters`);
    }
    if (target.kind === "token" && target.value.length > 4096) {
      errors.push("device token exceeds 4096 characters");
    }
  });

  if (form.imageUrl && !/^https:\/\//i.test(form.imageUrl)) {
    errors.push("Notification image must be an https URL");
  }

  if (form.scheduleMode === "SCHEDULED" && !form.scheduledAt) {
    errors.push("Select the date and time to send");
  } else if (scheduleTooSoon(form)) {
    errors.push(SCHEDULE_LEAD_MESSAGE);
  }

  const ttl = parseFloat(form.ttlHours);
  if (form.ttlHours !== "" && (isNaN(ttl) || ttl < 0 || ttl > 672)) {
    errors.push("Message lifetime (TTL) must be between 0 and 672 hours (28 days)");
  }

  const badge = parseInt(form.iosBadge, 10);
  if (form.iosBadge !== "" && (isNaN(badge) || badge < 0)) {
    errors.push("Badge must be an integer of 0 or more");
  }

  return errors;
};

export const initialForm = {
  /*
   * Which back end this form is filling in.
   *
   * CUSTOMER  - the notification centre (be-notification). The audience is chosen by customer
   *             scope and demographics, the message lands in the in-app inbox as well as being
   *             pushed, and Reach / View are counted against it.
   * FIREBASE  - a raw FCM send (be-admin -> be-notification /push/notification). No inbox row and
   *             nothing to count, but it can aim at a topic or a device token directly, which the
   *             notification centre deliberately cannot.
   */
  deliveryMode: "CUSTOMER",
  // 1. Notification
  title: "",
  body: "",
  imageUrl: "",
  campaignName: "",
  // 1b. Notification centre fields (CUSTOMER mode)
  status: "ACTIVE",
  contentType: "GENERAL",
  notificationType: "NORMAL",
  actionType: "NONE",
  actionLink: "",
  actionLabel: "",
  // 2. Target
  targetMode: "ALL",
  platforms: { ios: true, android: true },
  topics: "",
  users: "",
  tokens: "",
  // 2b. Audience of the notification centre (CUSTOMER mode)
  userScope: "ALL",
  scopeEmails: "",
  scopeCustomerUuids: "",
  scopeGender: "",
  scopeAgeMin: "",
  scopeAgeMax: "",
  // 3. Scheduling
  scheduleMode: "NOW",
  scheduledAt: null,
  // 4. Conversion events
  conversionEvent: "",
  // 5. Additional options
  dataType: "",
  deeplink: "",
  androidChannelId: "",
  androidIcon: "",
  androidColor: "",
  priority: "high",
  ttlHours: "",
  sound: true,
  iosBadge: "",
  contentAvailable: false,
  customData: [{ key: "", value: "" }]
};

/* ------------------------------------------------------------------------------------------
 * Notification centre (CUSTOMER mode)
 *
 * A different back end with a different body: POST / PUT /api/v1/admin/notifications on
 * th-b2c-techupgrade-be-notification. It resolves the audience itself from `userScope` and
 * `scopeFilter`, writes an inbox row per recipient and then pushes; this form never names a
 * device. Everything below used to live in the separate Notification detail page.
 * ------------------------------------------------------------------------------------------ */

/** An ISO instant from the form's Date, or null for "send now". */
const toIsoOrNull = value => {
  if (!value) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  return isNaN(date.getTime()) ? null : date.toISOString();
};

export const buildScopeFilter = form => {
  const filter = {};

  if (form.userScope === "EMAIL_LIST") {
    filter.emails = splitLines(form.scopeEmails);
  }
  if (form.userScope === "CUSTOMER_LIST") {
    filter.customerUuids = splitLines(form.scopeCustomerUuids);
  }
  if (form.scopeGender) {
    filter.gender = splitLines(form.scopeGender).map(item => item.toUpperCase());
  }
  if (form.scopeAgeMin !== "") {
    filter.ageMin = parseInt(form.scopeAgeMin, 10);
  }
  if (form.scopeAgeMax !== "") {
    filter.ageMax = parseInt(form.scopeAgeMax, 10);
  }

  return Object.keys(filter).length > 0 ? filter : undefined;
};

export const buildNotificationPayload = (form, sendNow) => {
  const scopeFilter = buildScopeFilter(form);

  return {
    status: form.status,
    contentType: form.contentType,
    notificationType: form.notificationType,
    // "Send now" and "schedule" are the same choice the Scheduling step already makes.
    scheduledAt: form.scheduleMode === "SCHEDULED" ? toIsoOrNull(form.scheduledAt) : null,
    name: (form.campaignName || "").trim(),
    header: (form.title || "").trim(),
    content: (form.body || "").trim(),
    imageUrl: (form.imageUrl || "").trim() || null,
    actionType: form.actionType,
    actionLink: (form.actionLink || "").trim() || null,
    actionLabel: (form.actionLabel || "").trim() || null,
    userScope: form.userScope,
    ...(scopeFilter ? { scopeFilter } : {}),
    ...(sendNow ? { sendNow: true } : {})
  };
};

export const validateNotificationForm = form => {
  const errors = [];

  if (!(form.campaignName || "").trim()) {
    errors.push("Notification name (internal) is required");
  }
  if (!(form.title || "").trim()) {
    errors.push("Notification title is required");
  }
  if (!(form.body || "").trim()) {
    errors.push("Notification message is required");
  }
  if ((form.imageUrl || "").trim() && !/^https:\/\//i.test(form.imageUrl.trim())) {
    errors.push("Image must be a URL starting with https://");
  }
  if (form.actionType !== "NONE" && form.actionType !== "SURVEY" && !(form.actionLabel || "").trim()) {
    errors.push("Button label is required");
  }
  if (form.actionType !== "NONE" && !(form.actionLink || "").trim()) {
    errors.push("When a button is selected, its destination is required");
  }
  if (form.actionType === "URL" && !/^https:\/\//i.test((form.actionLink || "").trim())) {
    errors.push("A URL destination must start with https://");
  }
  if (form.userScope === "EMAIL_LIST" && splitLines(form.scopeEmails).length === 0) {
    errors.push("When sending by email, enter at least 1 email");
  }
  if (form.userScope === "CUSTOMER_LIST" && splitLines(form.scopeCustomerUuids).length === 0) {
    errors.push("When sending by customer list, enter at least 1 customer uuid");
  }

  const ageMin = parseInt(form.scopeAgeMin, 10);
  const ageMax = parseInt(form.scopeAgeMax, 10);
  if (form.scopeAgeMin !== "" && form.scopeAgeMax !== "" && ageMin > ageMax) {
    errors.push("Invalid age range (minimum age is greater than maximum age)");
  }
  if (form.scheduleMode === "SCHEDULED" && !form.scheduledAt) {
    errors.push("When scheduling, a date and time is required");
  } else if (scheduleTooSoon(form)) {
    errors.push(SCHEDULE_LEAD_MESSAGE);
  }

  return errors;
};

/*
 * The same checks as validateNotificationForm, but keyed by the form field they belong to,
 * so the screen can show the message under the input itself and jump to it. One message per
 * field (the first that applies); the summary list still comes from validateNotificationForm.
 */
export const notificationFieldErrors = form => {
  const errors = {};
  if (!(form.campaignName || "").trim()) {
    errors.campaignName = "Notification name (internal) is required";
  }
  if (!(form.title || "").trim()) {
    errors.title = "Notification title is required";
  }
  if (!(form.body || "").trim()) {
    errors.body = "Notification message is required";
  }
  if ((form.imageUrl || "").trim() && !/^https:\/\//i.test(form.imageUrl.trim())) {
    errors.imageUrl = "Image must be a URL starting with https://";
  }
  if (form.actionType !== "NONE" && form.actionType !== "SURVEY" && !(form.actionLabel || "").trim()) {
    errors.actionLabel = "Button label is required";
  }
  if (form.actionType !== "NONE" && !(form.actionLink || "").trim()) {
    errors.actionLink = "When a button is selected, its destination is required";
  } else if (form.actionType === "URL" && !/^https:\/\//i.test((form.actionLink || "").trim())) {
    errors.actionLink = "A URL destination must start with https://";
  }
  if (form.userScope === "EMAIL_LIST" && splitLines(form.scopeEmails).length === 0) {
    errors.scopeEmails = "When sending by email, enter at least 1 email";
  }
  if (form.userScope === "CUSTOMER_LIST" && splitLines(form.scopeCustomerUuids).length === 0) {
    errors.scopeCustomerUuids = "When sending by customer list, enter at least 1 customer uuid";
  }
  const ageMin = parseInt(form.scopeAgeMin, 10);
  const ageMax = parseInt(form.scopeAgeMax, 10);
  if (form.scopeAgeMin !== "" && form.scopeAgeMax !== "" && ageMin > ageMax) {
    errors.scopeAgeMax = "Invalid age range (minimum age is greater than maximum age)";
  }
  if (form.scheduleMode === "SCHEDULED" && !form.scheduledAt) {
    errors.scheduledAt = "When scheduling, a date and time is required";
  } else if (scheduleTooSoon(form)) {
    errors.scheduledAt = SCHEDULE_LEAD_MESSAGE;
  }
  return errors;
};

/** The form as the notification centre stored it, for ?id= edits. */
export const formFromNotification = notification => {
  const scopeFilter = notification.scopeFilter || {};
  return {
    ...initialForm,
    deliveryMode: "CUSTOMER",
    title: notification.header || "",
    body: notification.content || "",
    imageUrl: notification.imageUrl || "",
    campaignName: notification.name || "",
    status: notification.status || "ACTIVE",
    contentType: notification.contentType || "GENERAL",
    notificationType: notification.notificationType || "NORMAL",
    actionType: notification.actionType || "NONE",
    actionLink: notification.actionLink || "",
    actionLabel: notification.actionLabel || "",
    userScope: notification.userScope || "ALL",
    scopeEmails: (scopeFilter.emails || []).join("\n"),
    scopeCustomerUuids: (scopeFilter.customerUuids || []).join("\n"),
    scopeGender: (scopeFilter.gender || []).join(", "),
    scopeAgeMin: scopeFilter.ageMin === undefined ? "" : String(scopeFilter.ageMin),
    scopeAgeMax: scopeFilter.ageMax === undefined ? "" : String(scopeFilter.ageMax),
    scheduleMode: notification.scheduledAt ? "SCHEDULED" : "NOW",
    scheduledAt: notification.scheduledAt ? new Date(notification.scheduledAt) : null
  };
};
